import React, { useEffect, useState } from "react";
import { Button, DropdownMenu, FormInput } from "@/components/bnp-ui";
import { BriefCase, LiveInspection } from "@/components/bnp-icons";
import Bundles from "@/components/pages/user/dashboard/properties/Tabs/live-inspection/Bundles";
import { useDisclosure } from "@mantine/hooks";
import { Checkbox } from "@mantine/core";
import { BNPModal } from "@/components/bnp-ui/overlays/BNPModal";
import { formatNumber } from "@/utils/formatNumber";
import parseHttpError from "@/utils/parseHttpError";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PropertyService, TransactionsService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Link from "next/link";
import useAuthToken from "@/hooks/useAuthToken";
import NewUser from "./NewUser";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import { PropertyInspectionModal } from "@/components/pages/user/dashboard/properties/Tabs/live-inspection/PropertyInspectionRequestModal";
import { buildSignInUrl } from "@/utils/helpers/authReturn";
import { useCurrency } from "@/hooks/useCurrency";

interface PropertyDetailCardProps {
  realtorName?: string;
  realtorJoinedYears?: string;
  phone?: string;
  propertyPrice?: any;
  totalPackage?: any;
  propertyId: string;
  requestInspection?: any;
  currency: any;
  serviced: any;
  shared: any;
  is_negotiable: any;
  has_requested: any;
  onViewProperties?: () => void;
}

const runtime = getRuntimeEnvironment();

const PropertyDetailCard = ({
  realtorName,
  totalPackage,
  realtorJoinedYears,
  propertyPrice,
  propertyId,
  requestInspection,
  is_negotiable,
  phone,
  currency,
  has_requested,
  onViewProperties,
}: PropertyDetailCardProps) => {
  const { user, isLoading: isAuthLoading } = useAuthToken();
  const router = useRouter();
  const [opened, { open, close }] = useDisclosure(false);
  const [newUserState, setNewUserState] = useState(true);
  const [termsChecked, setTermsChecked] = useState(false);
  const [requestedInspection, setRequestedInspection] =
    useState(requestInspection);
  const [transactionId, setTransactionId] = useState("");
  const [selectedSource, setSelectedSource] = useState("Bundle");
  const [walletDetails, setWalletDetails] = useState<any>();
  const inspectionAmount = 59900;
  const [bundleBalance, setBundleBalance] = useState(0);

  const [liveInspectionOpen, setLiveInspectionOpen] = useState<boolean>(false);

  const toggleLiveInspection = () => {
    setLiveInspectionOpen((prev) => !prev);
  };

  // Return mechanism: when the user is sent back here after login with
  // ?inspection=1, re-open the inspection modal. Wait for auth hydration so a
  // logged-in user isn't mistaken for a logged-out one; always strip the flag.
  useEffect(() => {
    if (router.query.inspection !== "1" || isAuthLoading) return;

    if (user) {
      setLiveInspectionOpen(true);
    }

    const [pathname, search] = router.asPath.split("?");
    const params = new URLSearchParams(search);
    params.delete("inspection");
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, undefined, {
      shallow: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.inspection, user, isAuthLoading]);

  ///bundle modal
  const [
    bundleModalOpened,
    { open: openBundleModal, close: closeBundleModal },
  ] = useDisclosure(false);
  const [view, setView] = useState<"bundles" | "ads">("bundles");

  useEffect(() => {
    console.log("New user state:", newUserState);
    if (user) {
      getUserBundleBalance();
    }
  }, [newUserState, user]);

  const defaultValues = {
    propertyId,
    paymentSource: selectedSource?.toLowerCase(),
    transactionId: transactionId,
  };

  const validationSchema = yup.object().shape({
    propertyId: yup.string().required("Property id is required"),
    paymentSource: yup.string().required("Payment source is required"),
    transactionId: yup.string(),
  });

  const {
    getValues,
    control,
    handleSubmit,
    setError,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const handleSourceChange = (source: any) => {
    setSelectedSource(source.label);
    setValue("paymentSource", source.id);
  };

  const liveInspectionRequest = async () => {
    let txnId = transactionId;

    if (selectedSource === "Wallet") {
      try {
        const response = await TransactionsService.DebitFNP({
          amount: inspectionAmount,
        });
        txnId = response.data;
        setTransactionId(txnId);
      } catch (error: any) {
        console.error(error);
        throw new Error(parseHttpError(error).message);
      }
    }

    try {
      if (selectedSource === "Bundle" && bundleBalance <= 0) {
        throw new Error("Insufficient bundle balance for this request.");
      }
      const payload = { transactionId: txnId, ...getValues() };
      const altPayload = getValues();
      const response = await PropertyService.RequestLiveInspection(
        selectedSource === "Bundle" ? payload : altPayload,
      );
      return response?.data;
    } catch (error: any) {
      console.error(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation: any = useMutation({
    mutationFn: liveInspectionRequest,
    onSuccess: (res: any) => {
      console.log(res?.data);
      toast.success("Live inspection requested!");
      close();
      window.location.reload();
    },
    onError: (error: any) => {
      toast.error(error.message || "Request failed");
    },
  });

  const paystackRequest = async () => {
    try {
      const response = await TransactionsService.InitiateTransaction({
        customerEmail: user?.email,
        amount: inspectionAmount,
        callback_url: `http://localhost:3000/property/${propertyId}?paymentSource=${
          getValues().paymentSource
        }`,
      });
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const paystackMutation: any = useMutation({
    mutationFn: paystackRequest,
    onSuccess: (res: any) => {
      router.push(`${res?.data?.authorization_url}`);
      close();
    },
    onError: (error: any) => {
      toast.error(error.message || "Request failed");
    },
  });

  const getWalletDetailsRequest = async () => {
    try {
      const response = await TransactionsService.GetWallet();
      // console.log(response?.data);
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: walletData,
    isLoading: walletDataIsLoading,
    isError: walletDataIsError,
    refetch: walletDataRefetch,
    isSuccess: walletDataIsSuccess,
  } = useQuery({
    queryKey: ["walletDetails"],
    queryFn: getWalletDetailsRequest,
    // Wallet is user-scoped — skip for anonymous visitors.
    enabled: !!user,
  });

  useEffect(() => {
    if (walletData) {
      setWalletDetails(walletData?.data);
    }
  }, [walletData]);

  const onSubmit = (e: any) => {
    e.preventDefault();
    if (!termsChecked) {
      toast.error("Please accept all terms and conditions");
      return;
    }

    if (selectedSource === "Paystack") {
      paystackMutation.mutate();
    } else {
      mutation.mutate();
    }
  };

  const getUserBundleBalance = async () => {
    try {
      const response = await TransactionsService.GetUserBundleBalance();
      const bundleBal = response?.data?.data?.bundleBalance || 0;
      setBundleBalance(bundleBal);
      return response?.data;
    } catch (error: any) {
      console.log(error);
      setBundleBalance(0);
      return null;
    }
  };

  const { data, isLoading, isError, refetch, isSuccess } = useQuery({
    queryKey: [`bundle-balance`],
    queryFn: getUserBundleBalance,
    enabled: !!user,
    staleTime: 60 * 60 * 1000,
  });

  const { formatPrice } = useCurrency();

  return (
    <div className=" w-[100%] lg:w-[352px] bg-weak-100 border border-soft-200  max-w-[352px] p-6 rounded-lg font-sans">
      <div className="flex flex-col items-center mb-5">
        <p>Property Price</p>
        <div className="flex text-2xl font-bold text-blue-primary mb-6 ">
          {propertyPrice !== null && propertyPrice !== undefined
            ? formatPrice(propertyPrice, currency)
            : "Price on request"}{" "}
          {is_negotiable &&
            propertyPrice !== null &&
            propertyPrice !== undefined && (
              <p className="ml-1 text-[10px] text-green-primary">Negotiable</p>
            )}
        </div>
        <p className="mb-6">
          Total package:{" "}
          {totalPackage !== null && totalPackage !== undefined
            ? formatPrice(totalPackage, currency)
            : "Price on request"}{" "}
        </p>

        <div className="flex flex-col gap-[10px]">
          <Button
            className="bg-bnp-cyan text-white text-sm font-semibold w-[254px] h-[37px] rounded-lg"
            onClick={() => {
              PropertyService.TrackPropertyInsight(
                propertyId,
                "whatsapp_contact",
              );

              const formattedNumber = phone?.startsWith("0")
                ? "234" + phone.substring(1)
                : phone;

              const propertyUrl = window.location.href;
              const message = encodeURIComponent(
                `Hello, I would like to inquire about your property on FindNigeriaProperties.com. ${propertyUrl}.`,
              );
              const whatsappUrl = `https://wa.me/${formattedNumber}?text=${message}`;

              window.open(whatsappUrl, "_blank");
            }}
          >
            Contact Realtor On Whatsapp
          </Button>
          {requestInspection && (
            <Button
              className="bg-white text-blue-primary border border-bnp-cyan text-sm font-semibold w-[254px] h-[37px] rounded-lg"
              onClick={open}
              disabled={has_requested}
            >
              {has_requested
                ? " Property Inspection Requested"
                : " Request Property Inspection"}
            </Button>
          )}

          {!runtime.isProduction && requestInspection && (
            <Button
              className="bg-white text-blue-primary border border-bnp-cyan text-sm font-semibold w-[254px] h-[37px] rounded-lg"
              onClick={toggleLiveInspection}
              disabled={has_requested}
            >
              {has_requested
                ? "Property Inspection Requested"
                : " Request Property Inspection V2"}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Realtor</span>
          <span className="text-blue-primary font-bold max-w-[70%] text-right">
            {realtorName}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600">Registered</span>
          <span className="text-blue-primary font-bold">
            {realtorJoinedYears}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600">Phone</span>
          <span className="text-blue-primary font-bold">
            {phone ? phone : "-"}
          </span>
        </div>
      </div>

      <button
        onClick={onViewProperties}
        className="w-full mt-6 py-2 text-sm text-bnp-cyan underline text-center transition-colors"
      >
        View all properties from this agent
      </button>

      <BNPModal isOpen={opened} onClose={close}>
        <div className="flex p-4 gap-4 items-center w-full">
          <LiveInspection />
          <div className="flex flex-col gap-1">
            <h1 className="text-blue-primary font-medium text-lg">
              Request Property Inspection
            </h1>
            <p className="text-grey-7 text-sm font-normal">
              Get real-time video tours, detailed reports, and professional
              advice—directly from our experts
            </p>
          </div>
        </div>
        {newUserState ? (
          <NewUser setNewUser={setNewUserState} />
        ) : user ? (
          <div className="w-full h-fit p-4">
            <div className="p-3 flex flex-col gap-2 w-full h-fit border border-bnp-cyan bg-bnp-teal rounded-lg">
              <h2 className="text-main-900 font-semibold text-sm">
                What is a bundle?
              </h2>
              <p className="text-sm font-normal text-grey-7">
                A bundle is a subscription for a minimum number of live
                inspection services starting from five (5) live inspection
                services. Choose higher bundles to enjoy great discounts!
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-soft-200 py-3 px-4 w-full h-fit my-4 rounded-[16px]">
              <div className="flex flex-col gap-2">
                <p className="text-sm text-grey-7">Available Bundle</p>
                <p className="font-bold text-main-900 text-base">
                  {data?.data?.bundleBalance}
                </p>
              </div>
              <Button
                className="border border-bnp-dark font-semibold text-bnp-dark bg-white text-sm px-4 py-2 rounded-lg"
                onClick={() => {
                  setView("bundles");
                  openBundleModal();
                }}
              >
                Top up bundle
              </Button>
            </div>

            <div className="w-full mb-6">
              <p className="text-sm font-semibold text-main-900 mb-2">
                Amount per inspection
              </p>
              <div className="flex items-center text-gray-500 p-4 cursor-not-allowed bg-gray-100 w-full h-[52px] rounded-lg">
                <p>₦59,900</p>
              </div>
            </div>

            <form
              action="submit"
              className="flex flex-col"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="flex flex-col lg:flex-row gap-4 pb-5">
                <div className="w-full">
                  {/* Any commented inputs stay unchanged */}
                  <div className="w-full mt-2">
                    <p className="text-sm font-semibold text-main-900 mb-4">
                      Select source of payment
                    </p>
                    <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                      {[
                        { id: "bundle", label: "Bundle" },
                        { id: "wallet", label: "Wallet" },
                        { id: "single_payment", label: "Paystack" },
                      ].map((option) => (
                        <div
                          key={option.id}
                          className="relative flex items-center justify-center border rounded-lg p-4 w-full h-16 transition-colors ease-linear delay-200 cursor-pointer hover:border-bnp-cyan"
                          onClick={() => handleSourceChange(option)}
                        >
                          <div className="flex items-center">
                            <div
                              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mr-2 ${
                                selectedSource === option.label
                                  ? "border-bnp-cyan"
                                  : "border-gray-300"
                              }`}
                            >
                              {selectedSource === option.label && (
                                <div className="w-4 h-4 rounded-full bg-bnp-cyan"></div>
                              )}
                            </div>
                            <span className="text-gray-700 font-medium">
                              {option.label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedSource === "Bundle" &&
                      data?.data?.bundleBalance === 0 && (
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-orange-50 p-4 rounded-lg mt-4 gap-4">
                          <div className="flex items-start">
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                              <span className="text-orange-500 font-bold">
                                i
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-main-900">
                                No inspection tickets left in your bundle
                                balance
                              </p>
                              <p className="text-grey-7 text-sm">
                                Please purchase a bundle to proceed
                              </p>
                            </div>
                          </div>
                          <Link
                            href={`/user/dashboard/properties/my-subscriptions`}
                          >
                            <Button className="bg-bnp-dark font-semibold text-white text-sm px-4 py-2 rounded-lg">
                              Purchase bundle
                            </Button>
                          </Link>
                        </div>
                      )}

                    {selectedSource === "Wallet" && (
                      <div>
                        <div className="flex gap-2 text-sm text-grey-7 mt-4">
                          <p>Wallet balance:</p>
                          <p>
                            ₦
                            {walletDetails?.isWalletAvailable
                              ? walletDetails?.wallet?.available_balance
                              : "0"}
                          </p>
                        </div>

                        {walletDetails?.isWalletAvailable &&
                          walletDetails?.wallet?.available_balance <
                            inspectionAmount && (
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-orange-50 p-4 rounded-lg mt-4 gap-4">
                              <div className="flex items-start">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                                  <span className="text-orange-500 font-bold">
                                    i
                                  </span>
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-main-900">
                                    Insufficient funds for the action
                                  </p>
                                  <p className="text-grey-7 text-sm">
                                    Please fund your wallet to finish the
                                    request process
                                  </p>
                                </div>
                              </div>
                              <Link href={`/user/dashboard/payments`}>
                                <Button className="bg-bnp-dark font-semibold text-white text-sm px-4 py-2 rounded-lg">
                                  Fund wallet
                                </Button>
                              </Link>
                            </div>
                          )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-7 w-full mb-[50px]">
                    <Checkbox
                      color="cyan"
                      checked={termsChecked}
                      onChange={(event) =>
                        setTermsChecked(event.currentTarget.checked)
                      }
                    />
                    <p className="text-sm text-grey-7">
                      I hereby attest to the rules of Live Inspection Service as
                      managed by FindNigeriaProperties.com
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-4 mb-8">
                <div className="flex justify-center w-full max-w-md">
                  <Button
                    type="submit"
                    className="bg-bnp-dark text-white text-sm font-semibold w-full h-[60px] rounded-lg"
                    onClick={onSubmit}
                    isLoading={mutation.isPending || paystackMutation.isPending}
                    disabled={
                      selectedSource === "Wallet" &&
                      walletDetails?.wallet?.available_balance <
                        inspectionAmount
                    }
                  >
                    Request Live Inspection
                  </Button>
                </div>
              </div>
            </form>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-6 text-center gap-4">
            <h2 className="text-main-900 font-semibold text-lg">
              You need to be logged in
            </h2>
            <p className="text-grey-7 text-sm max-w-md">
              Please log in to request a live inspection or access your bundle.
            </p>
            <Link
              href={buildSignInUrl(router.asPath)}
              className="w-full max-w-xs"
            >
              <Button className="bg-bnp-dark text-white text-sm font-semibold w-full h-[60px] rounded-lg">
                Go to Login
              </Button>
            </Link>
          </div>
        )}
      </BNPModal>
      <BNPModal isOpen={bundleModalOpened} onClose={closeBundleModal}>
        <>
          <div className="flex p-4 gap gap-4 items-center border-b border-soft-200 w-[100%] lg:w-[790px]">
            <LiveInspection />
            <div className="flex flex-col gap-1">
              <h1 className="text-blue-primary font-medium text-lg">
                Live Inspection Bundles
              </h1>
              <p className="text-grey-7 text-sm font-normal">
                Buy live inspection bundles for later use
              </p>
            </div>
          </div>
          <div className="bg-white h-[542px]  w-[100%] lg:w-[790px] p-2 flex flex-col gap-4 mb-20">
            {view === "bundles" ? <Bundles /> : "Ad"}
          </div>
        </>
      </BNPModal>
      <PropertyInspectionModal
        isRald={false}
        isOpen={liveInspectionOpen}
        onClose={toggleLiveInspection}
        propertyTitle={""}
        propertyAddress={""}
        propertyId={propertyId}
      />
    </div>
  );
};

export default PropertyDetailCard;
